import { useEffect, useRef, useState, type KeyboardEvent, type MouseEvent } from 'react';
import { Icono } from '@/components/Layout/iconos';
import { descargarBlob } from '@/lib/cuadro/descargar';
import { filasExportables, hojaExportacion, nombreExportacion } from '@/lib/cuadro/exportar';
import { textoSinLlamadas, type FiltrosCuadro } from '@/lib/cuadro/filtros';
import type { LlamadaFeedRow } from '@/types/llamadas';

// Exportación del periodo que se está mirando (change 003), con sus filtros. El
// Excel lleva todas las llamadas; el PDF es la propia página impresa (D6).

// Título que tenía la pestaña antes de imprimir. A nivel de módulo, y guardado solo
// una vez: con dos impresiones seguidas, la segunda guardaría como «original» el
// nombre de la exportación y la pestaña se quedaría con él para siempre.
let tituloAntesDeImprimir: string | null = null;

function ponerTituloDeImpresion(nombre: string) {
  tituloAntesDeImprimir ??= document.title;
  document.title = nombre;
}

function restaurarTitulo() {
  if (tituloAntesDeImprimir !== null) document.title = tituloAntesDeImprimir;
  tituloAntesDeImprimir = null;
}

const BOTON =
  'flex items-center gap-1.5 rounded-[var(--radius-sm)] border border-[var(--border)] bg-[var(--surface)] px-3 py-1.5 text-[12.5px] font-medium text-[var(--text-2)] cursor-pointer hover:bg-[var(--surface-2)] hover:text-[var(--navy)] focus-visible:outline-none focus-visible:shadow-[var(--ring)] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-[var(--surface)] disabled:hover:text-[var(--text-2)] aria-disabled:cursor-wait aria-disabled:opacity-60';

type FalloExcel = 'carga' | 'generar' | null;

// Enter mantenido repite el `keydown`, y cada repetición activa el botón: sin frenarlo
// descarga un Excel —o abre un diálogo de impresión— por repetición. Antes lo tapaba el
// `disabled`, que quitaba el foco (AD-P2-2).
//
// Espacio es otra cosa y la mitad que lo cubre es defensa por simetría, no un arreglo:
// un botón se activa al SOLTAR el espacio, así que mantenerlo no dispara nada y quitar
// esa mitad no pone rojo ningún test (comprobado). Se queda por dos razones: las dos
// teclas que activan un botón se tratan igual aquí, y si alguna plataforma o ayuda
// técnica repitiera el par keydown+keyup, cancelar el keydown repetido cancela también
// esa activación. El e2e mantiene las dos teclas y comprueba un solo fichero; de Espacio
// eso ya se cumplía antes.
function frenarRepeticion(e: KeyboardEvent<HTMLButtonElement>) {
  if (e.repeat && (e.key === 'Enter' || e.key === ' ')) e.preventDefault();
}

// El segundo clic de un doble clic no es otra acción. La guarda de «en curso» no lo
// para: lo que hacen estos botones termina antes de que llegue ese clic.
const clicRepetido = (e: MouseEvent<HTMLButtonElement>) => e.detail > 1;

// La misma pantalla se escribe de varias maneras y todas son ella, así que esto imita
// cómo lo decide el router, que es quien manda: `useMatch` le pasa a `matchPath` la
// dirección pasada por su `decodePath` (`@remix-run/router`), y `matchPath` perdona por
// su cuenta las mayúsculas y la barra final. Las escrituras que da por el cuadro son
// infinitas (cualquier letra se puede escribir con su porcentaje); estas ocho, que entre
// todas cubren los tres pasos —decodificar por segmento, minúsculas y quitar barras
// finales—, tienen que salir iguales de aquí: `/cuadro`, `/cuadro/`, `/cuadro//`,
// `/Cuadro`, `/CUADRO`, `/cuadr%6F`, `/cuadr%6F/` y `/%43uadro`. Chrome no normaliza el
// porcentaje: `location.pathname` sigue diciendo `/cuadr%6F/`.
//
// `decodePath` decodifica POR SEGMENTO y vuelve a escapar la barra que salga de
// decodificar, justo para que un segmento no se parta en dos. Aquí igual: decodificando
// el pathname entero, `%2F` se convertía en barra y `/cuadro%2F` se leía como `/cuadro`
// cuando el router NO la casa —se va al `*`—, así que el fichero se entregaba en una
// pantalla que no era la que lo pidió.
//
// Sin normalizar nada, estando en `/cuadro/` el enlace del menú empuja `/cuadro` —misma
// pantalla, mismo bloque montado, la misma persona esperando— y la exportación en vuelo
// se descartaba ahí, sin fichero y sin aviso. Hay e2e de exportación entrando por
// `/cuadro/`, `/Cuadro` y `/cuadr%6F/`, tres escrituras que entre ellas tocan los tres
// pasos (de las dos últimas hay además e2e de impresión, el 037). Se exporta con el prefijo `_` de los
// internos del repo porque es la única señal de cancelación que queda y su tabla de
// formas se prueba en la unit.
export function _rutaNormalizada(pathname: string): string {
  let ruta = pathname;
  try {
    ruta = pathname
      .split('/')
      .map((segmento) => decodeURIComponent(segmento).replace(/\//g, '%2F'))
      .join('/');
  } catch {
    // Un porcentaje mal formado (`/%ZZ`) hace lanzar a `decodeURIComponent`, y entonces
    // el router también se queda con la dirección sin decodificar. Identifica peor, pero
    // una dirección imposible no puede tirar por tierra un fichero que se estaba
    // generando.
  }
  return ruta.toLowerCase().replace(/\/+$/, '');
}

// ¿Se ha quedado sin destinatario el fichero que se está generando? La señal es la
// dirección desde la que se pidió, comparada al resolver el `await`, y NO el
// desmontaje de este bloque: `CuadroPage` lo pinta dentro de `!cargando`, así que un
// cambio de periodo lo desmonta y lo vuelve a montar mientras quien pidió el Excel
// sigue en el cuadro esperándolo, y cortar por desmontaje le quitaba el fichero sin
// decir nada (vuelta 4 del gate). Los filtros NO desmontan nada —son estado de React
// y no tocan `cargando`—; «Reintentar» y «Cargar de todos modos» sí, pero solo se
// pueden pulsar cuando este bloque no está en pantalla.
//
// En otra pantalla el fichero llegaría por sorpresa y, si fallara, el aviso no tendría
// dónde verse. La dirección vale como identidad porque el cuadro no guarda nada en la
// URL: el periodo y los filtros son estado de React, así que solo cambia al navegar.
//
// Cerrar la sesión no tiene señal propia. La tuvo —una marca de módulo— y salía peor:
// un `signOut` que falla por red devuelve el error sin avisar a nadie y sin borrar la
// sesión, así que la marca se quedaba puesta y cancelaba en silencio TODA exportación
// posterior, con la persona dentro del panel. Al salir, la dirección pasa a `/login` y
// eso ya corta. Queda fuera la ventana en la que ya se ha pulsado «Salir» y el servidor
// todavía no ha contestado —lo que tarde `signOut`, unos cientos de milisegundos—:
// dentro de ella la dirección sigue siendo la del cuadro, así que un fichero pedido
// justo antes puede llegar mientras se sale. Límite aceptado por Sergio.
function seQuedoSinDestinatario(rutaQueLoPidio: string): boolean {
  return _rutaNormalizada(window.location.pathname) !== rutaQueLoPidio;
}

function registrarFallo(evt: string, e: unknown) {
  console.error(JSON.stringify({ evt, message: e instanceof Error ? e.message : String(e) }));
}

export function BotonesExportar({
  llamadas,
  periodoVacio,
  diaDesde,
  diaHasta,
  filtros,
  razonSocialCliente,
}: {
  llamadas: LlamadaFeedRow[];
  periodoVacio: boolean;
  diaDesde: string;
  diaHasta: string;
  filtros: FiltrosCuadro;
  razonSocialCliente: string | null;
}) {
  const [generando, setGenerando] = useState(false);
  const [fallo, setFallo] = useState<FalloExcel>(null);
  // Lo que oye un lector de pantalla: el texto del botón cambia sin que nadie lo diga.
  const [anuncio, setAnuncio] = useState('');
  // Cada exportación anuncia en un nodo nuevo: la segunda dice lo mismo que la
  // primera («Excel descargado»), y un texto que no cambia no se vuelve a anunciar.
  const [exportacion, setExportacion] = useState(0);
  // Mientras se genera, el botón no se deshabilita de verdad: un botón con foco que
  // pasa a `disabled` lo pierde, y quien usa el teclado vuelve al principio de la
  // página (AD-P2-2). `aria-disabled` no bloquea el clic, y el estado de React no se
  // ve hasta el siguiente render: esta guarda síncrona evita un segundo fichero
  // mientras se genera el primero.
  const enCurso = useRef(false);
  // Lo que se está mirando, como VALOR: los dos días y los cuatro campos del filtro. Por
  // identidad del objeto no servía —exportar con el filtro A, cambiar a B y VOLVER a A
  // daba un objeto distinto con la misma selección, así que se tragaba un aviso que sí
  // tocaba: ni fichero ni explicación—. Con `JSON.stringify` y no con una plantilla porque
  // distingue `null` de la cadena «null», que podría ser el nombre de una rama.
  const seleccionMirada = JSON.stringify([
    diaDesde,
    diaHasta,
    filtros.rama,
    filtros.motivo,
    filtros.desenlace,
    filtros.cliente,
  ]);
  // En un ref para poder leer el valor VIVO cuando una exportación termine: el de la
  // closure es el del render en que se pulsó.
  const seleccionActual = useRef(seleccionMirada);
  const vacio = llamadas.length === 0;
  const nombre = nombreExportacion({ diaDesde, diaHasta, filtros, razonSocialCliente });
  const porQueNo = vacio ? textoSinLlamadas(periodoVacio) : undefined;

  async function descargarExcel() {
    if (enCurso.current) return;
    enCurso.current = true;
    setGenerando(true);
    setFallo(null);
    setExportacion((n) => n + 1);
    setAnuncio('Generando el Excel…');
    try {
      // La pantalla desde la que se pide, apuntada antes de esperar a nada. Va DENTRO
      // del `try` para que la cubra el `finally`: hoy no puede lanzar, pero si lo
      // hiciera dejaría el botón clavado en «Generando…» y `enCurso` puesto, o sea
      // cancelando en silencio todo lo que se pidiera después. Esa es exactamente la
      // forma de fallo por la que se retiró la marca del cierre de sesión.
      const rutaQueLoPidio = _rutaNormalizada(window.location.pathname);
      // Y la selección, por lo mismo y para lo de abajo.
      const seleccionQueLoPidio = seleccionActual.current;
      // Carga diferida (T8): el escritor y fflate solo viajan si alguien exporta.
      // Su fallo es distinto del de generar, y se dice distinto: recargar arregla
      // el primero (el trozo desaparece tras un despliegue) y no el segundo.
      const escritor = await import('@/lib/cuadro/xlsx').catch((e: unknown) => {
        registrarFallo('exportar_excel_carga_failed', e);
        return null;
      });
      if (seQuedoSinDestinatario(rutaQueLoPidio)) {
        // La región de estado no puede seguir diciendo «Generando el Excel…» cuando ya
        // no se genera nada. Con la ruta como única señal esto no se llega a ver: el
        // corte solo salta si la dirección ha cambiado de verdad, y ese cambio desmonta
        // este bloque en el mismo render (en la ventana del cierre de sesión la
        // dirección sigue siendo la del cuadro, así que ahí no se corta: se entrega, y
        // es el límite aceptado). Se limpia igual, porque el día que haya un corte que
        // no desmonte la región se quedaría afirmando algo falso; y vaciarla no anuncia
        // nada nuevo, solo deja de decirlo.
        setAnuncio('');
        return;
      }
      if (!escritor) {
        setFallo('carga');
        setAnuncio('');
        return;
      }
      try {
        const hoja = hojaExportacion(filasExportables(llamadas));
        const fichero = escritor.blobXlsx(hoja);
        // Generar bloquea el hilo —medido: 491 ms con 50.000 filas—, y un clic que caiga
        // ahí espera en la cola: la comprobación de antes vio todavía el cuadro. Se cede
        // el turno para que ese clic se procese, y se vuelve a mirar antes de entregar.
        await new Promise((listo) => setTimeout(listo));
        if (seQuedoSinDestinatario(rutaQueLoPidio)) {
          setAnuncio('');
          return;
        }
        descargarBlob(fichero, `${nombre}.xlsx`);
        setAnuncio('Excel descargado');
      } catch (e) {
        registrarFallo('exportar_excel_failed', e);
        // El aviso dice «las llamadas de este periodo» y manda «probar con otros
        // filtros». Si la selección ya no es la que pidió el fichero, aparecería en
        // pantalla hablando de otra cosa: con el generador lento se puede exportar con el
        // filtro A, cambiar a B y fallar entonces, y el efecto que limpia el aviso ya
        // había corrido —limpia lo que hay, no lo que va a llegar—. Mismo criterio que la
        // ruta: se apunta al pedir y se comprueba al terminar.
        if (seleccionActual.current === seleccionQueLoPidio) setFallo('generar');
        setAnuncio('');
      }
    } finally {
      enCurso.current = false;
      setGenerando(false);
    }
  }

  // El aviso de GENERAR dice «las llamadas de este periodo» y «prueba con otros
  // filtros»: en cuanto cambia lo que se está mirando deja de referirse a lo que hay en
  // pantalla. Con el periodo daba igual —cambiarlo desmonta este bloque y el aviso se va
  // con él—, pero los filtros no desmontan nada y el texto se quedaba colgado de una
  // selección que ya no existía.
  //
  // La señal es la SELECCIÓN, no `nombre`: dos filtros distintos pueden dar el mismo
  // nombre de fichero —dos ramas sin ninguna letra válida, «💥» y «💣», salen las dos
  // como `_rama`, que es el límite aceptado en la vuelta 3— y entonces el efecto no
  // corría y el aviso se quedaba hablando de la anterior. Y se compara por VALOR: la
  // referencia de `filtros` no sirve, porque volver a la misma selección da un objeto
  // nuevo.
  //
  // El de CARGA no: no habla de periodos ni de filtros, dice «recarga la página», y su
  // causa —el trozo del Excel ya no existe tras un despliegue— no la arregla cambiar de
  // cliente. Borrarlo al mover un filtro quitaría de la pantalla un consejo que sigue
  // siendo el correcto, y el siguiente intento fallaría igual.
  useEffect(() => {
    seleccionActual.current = seleccionMirada;
    setFallo((f) => (f === 'generar' ? null : f));
  }, [seleccionMirada]);

  // El nombre que el navegador propone al «Guardar como PDF» sale del título de
  // la pestaña: se pone el de la exportación mientras dura la impresión (T3) y se
  // restaura después. Por cualquier camino: el botón o Ctrl+P.
  useEffect(() => {
    const antes = () => ponerTituloDeImpresion(nombre);
    window.addEventListener('beforeprint', antes);
    return () => window.removeEventListener('beforeprint', antes);
  }, [nombre]);
  useEffect(() => {
    window.addEventListener('afterprint', restaurarTitulo);
    return () => {
      window.removeEventListener('afterprint', restaurarTitulo);
      // Si el cuadro se desmonta a mitad de una impresión (una redirección de
      // sesión, por ejemplo), su `afterprint` ya no tendría quien lo atienda.
      restaurarTitulo();
    };
  }, []);

  // El botón lo pone también él, antes de abrir el diálogo, sin fiarse de que
  // llegue `beforeprint`.
  function descargarPdf() {
    ponerTituloDeImpresion(nombre);
    window.print();
  }

  return (
    <div className="ml-auto flex flex-col items-end gap-1">
      <div className="flex items-center gap-2">
        <button
          type="button"
          className={BOTON}
          disabled={vacio}
          aria-disabled={generando || undefined}
          title={porQueNo}
          onKeyDown={frenarRepeticion}
          onClick={(e) => {
            if (clicRepetido(e)) return;
            void descargarExcel();
          }}
        >
          <Icono nombre="download" className="h-4 w-4 shrink-0" />
          {generando ? 'Generando…' : 'Descargar Excel'}
        </button>
        <button
          type="button"
          className={BOTON}
          disabled={vacio}
          title={porQueNo}
          onKeyDown={frenarRepeticion}
          onClick={(e) => {
            if (clicRepetido(e)) return;
            descargarPdf();
          }}
        >
          <Icono nombre="printer" className="h-4 w-4 shrink-0" />
          Descargar PDF
        </button>
      </div>
      <p role="status" aria-live="polite" className="sr-only">
        <span key={exportacion}>{anuncio}</span>
      </p>
      {/* Sobre la superficie blanca, en su caja: sobre el gris de la página, esta tinta
          no llegaba al contraste AA (4,27:1; AD-P2-3). */}
      {fallo && (
        <p
          role="alert"
          className="m-0 max-w-[36rem] rounded-[var(--radius-sm)] border border-[var(--orange-border)] bg-[var(--surface)] px-2.5 py-1.5 text-[12px] text-[var(--orange-ink)]"
        >
          {fallo === 'carga'
            ? // «Recarga» y no «vuelve a intentarlo»: tras un despliegue, el trozo del
              // Excel que pide esta pestaña ya no existe, y reintentar no lo arregla.
              'No he podido cargar el generador del Excel. Recarga la página y vuelve a intentarlo; si sigue fallando, avísanos.'
            : 'No he podido generar el Excel con las llamadas de este periodo, y recargar no lo arregla. Avísanos; mientras tanto, prueba con otros filtros.'}
        </p>
      )}
    </div>
  );
}
